/**
 * GlowCard - Card with colored glow shadow, inner gradient, and highlights
 *
 * Features:
 * - Colored shadow (glow effect)
 * - Diagonal gradient background with color accent
 * - Radial light spot for depth
 * - Top highlight line
 * - Smooth hover transitions
 */

"use client";

import { ReactNode } from "react";

interface GlowCardProps {
  children: ReactNode;
  className?: string;
  /** Glow color variant */
  variant?: "pink" | "purple" | "blue" | "neutral";
}

const glowColors = {
  pink: {
    shadow:
      "shadow-[0_0_30px_-10px_rgba(236,72,153,0.4)] hover:shadow-[0_0_40px_-10px_rgba(236,72,153,0.6)]",
    highlight: "via-pink-400/20",
    gradientFrom: "from-pink-500/8",
    borderHover: "hover:border-pink-500/25",
  },
  purple: {
    shadow:
      "shadow-[0_0_30px_-10px_rgba(168,85,247,0.4)] hover:shadow-[0_0_40px_-10px_rgba(168,85,247,0.6)]",
    highlight: "via-purple-400/20",
    gradientFrom: "from-purple-500/8",
    borderHover: "hover:border-purple-500/25",
  },
  blue: {
    shadow:
      "shadow-[0_0_30px_-10px_rgba(59,130,246,0.4)] hover:shadow-[0_0_40px_-10px_rgba(59,130,246,0.6)]",
    highlight: "via-blue-400/20",
    gradientFrom: "from-blue-500/8",
    borderHover: "hover:border-blue-500/25",
  },
  neutral: {
    shadow:
      "shadow-[0_0_25px_-10px_rgba(156,163,175,0.3)] hover:shadow-[0_0_35px_-10px_rgba(156,163,175,0.5)]",
    highlight: "via-gray-400/15",
    gradientFrom: "from-gray-500/5",
    borderHover: "hover:border-gray-500/25",
  },
};

export default function GlowCard({
  children,
  className = "",
  variant = "pink",
}: GlowCardProps) {
  const colors = glowColors[variant];

  return (
    <div
      className={`
        relative overflow-hidden
        rounded-2xl
        border border-white/10
        ${colors.shadow}
        ${colors.borderHover}
        transition-all duration-300
        hover:-translate-y-1
        ${className}
      `}
    >
      {/* Subtle diagonal gradient */}
      <div
        className={`
          absolute inset-0
          bg-gradient-to-br ${colors.gradientFrom} via-transparent to-transparent
        `}
      />

      {/* Top highlight line */}
      <div
        className={`
          absolute top-0 left-6 right-6 h-[1px]
          bg-gradient-to-r from-transparent ${colors.highlight} to-transparent
        `}
      />

      {/* Content */}
      <div className="relative h-full">{children}</div>
    </div>
  );
}
